// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the review card the assistant posts before it creates a task or sends mail /
// WhatsApp. Shows a one-glance summary (kind badge, title, who / where, a text preview);
// the editable fields sit behind "Edit". Edits must be saved before Confirm so the
// server acts on exactly the revision the user saw. Automation reviews (`flow`) are
// read-only, except the words of a one-time email, which save as a new flow version.
import {Endpoints} from '@app/features/app/constants/Endpoints';
import styles from '@app/features/konvo_assistant/components/AssistantActionCard.module.css';
import {
	AgentActionFields,
	AgentActionOpen,
	AgentActionSummary,
	agentConfirmLabel,
	agentKindLabel,
	isAgentDraft,
} from '@app/features/konvo_assistant/components/AssistantAgentActionCard';
import {
	type FlowMailFields,
	FlowSummary,
	flowConfirmLabel,
	flowKindLabel,
	flowMailFields,
	isFlowMailCard,
} from '@app/features/konvo_assistant/components/AssistantFlowCard';
import {opKindIcon, opKindLabel} from '@app/features/konvo_assistant/components/AssistantOpCard';
import {WebFormFields, WebFormSummary} from '@app/features/konvo_assistant/components/AssistantWebFormCard';
import {KonvoCardHead} from '@app/features/konvo_assistant/components/card/KonvoCardHead';
import {actorHue, actorInitials} from '@app/features/konvo_notifications/utils/ClickUpInboxEvents';
import {openKonvoTask} from '@app/features/konvo_tasks/commands/KonvoTaskCommands';
import {SpaceTile, StatusPill} from '@app/features/konvo_tasks/components/workspace/shared/WorkspaceBits';
import KonvoTasks from '@app/features/konvo_tasks/state/KonvoTasks';
import KonvoWorkspace from '@app/features/konvo_tasks/state/KonvoWorkspace';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {
	CalendarBlankIcon,
	ChatCircleIcon,
	CheckCircleIcon,
	EnvelopeSimpleIcon,
	GlobeIcon,
	ImageIcon,
	PaperclipIcon,
	PencilSimpleIcon,
	TimerIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {formatScheduledMessage} from '@konvo/date_utils/src/DateFormatting';
import type {AssistantActionDraft} from '@konvo/schema/src/domains/konvo/AssistantActionDraft';
import type {AssistantFlowCardView} from '@konvo/schema/src/domains/konvo/AssistantFlow';
import {useLingui} from '@lingui/react/macro';
import {IconBolt, IconTerminal2} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

interface DraftResponse {
	/** Null on automation review cards, which carry `flow` instead; only one-time emails among them can be edited. */
	draft: AssistantActionDraft | null;
	flow?: AssistantFlowCardView;
	revision: number;
	note?: string | null;
	done: boolean;
	expired: boolean;
	options: Array<{id: string; name: string}>;
	/** Files the user sent that go out with this action. */
	files?: Array<AssistantFile>;
}

interface AssistantFile {
	id: string;
	filename: string;
	contentType: string;
	size: number;
}

function formatSize(size: number): string {
	if (size < 1024) return `${size} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`;
	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function optionName(options: Array<{id: string; name: string}> | undefined, id: string): string {
	return options?.find((option) => option.id === id)?.name ?? id;
}

function formatDue(value: string): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat(undefined, {
		weekday: 'short',
		day: 'numeric',
		month: 'short',
		hour: '2-digit',
		minute: '2-digit',
	}).format(date);
}

/** Old completed cards included an internal deep link in their status text. */
function displayNote(value: string): string {
	return value
		.replace(/\s*konvo:\/\/channels\/\S+/gi, '')
		.replace(/\s+/g, ' ')
		.trim();
}

const NOTE_URL = /https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"]/gi;

/** Splits a done note into text and links, so "Task created. https://app.clickup.com/t/…" can be opened. */
export function noteParts(value: string): Array<{text: string; href?: string}> {
	const parts: Array<{text: string; href?: string}> = [];
	let last = 0;
	for (const match of value.matchAll(NOTE_URL)) {
		const start = match.index ?? 0;
		if (start > last) parts.push({text: value.slice(last, start)});
		parts.push({text: match[0], href: match[0]});
		last = start + match[0].length;
	}
	if (last < value.length) parts.push({text: value.slice(last)});
	return parts;
}

function NoteText({value}: {value: string}) {
	const {t} = useLingui();
	return (
		<>
			{noteParts(displayNote(value)).map((part, index) => {
				if (!part.href) return <span key={index}>{part.text}</span>;
				const href = part.href;
				const clickUp = /^https?:\/\/([^/]+\.)?clickup\.com\//i.test(href);
				return (
					<a
						key={index}
						className={styles.noteLink}
						href={href}
						target="_blank"
						rel="noopener noreferrer"
						title={href}
						onClick={(event) => {
							event.preventDefault();
							void openExternalUrl(href);
						}}
					>
						{clickUp ? t`Open in ClickUp` : href}
					</a>
				);
			})}
		</>
	);
}

type TaskDraft = Extract<AssistantActionDraft, {kind: 'task' | 'task_update'}>;
type Options = Array<{id: string; name: string}>;

const CLICKUP_TASK_URL = /^https?:\/\/([^/]+\.)?clickup\.com\/t\/(?:\d+\/)?([^/?#\s]+)/i;

/** "Task created. https://app.clickup.com/t/86c…" → the text, the link and the task id. */
export function taskDoneNote(value: string): {text: string; url: string; taskId: string} | null {
	const parts = noteParts(displayNote(value));
	const link = parts.find((part) => part.href && CLICKUP_TASK_URL.test(part.href));
	if (!link?.href) return null;
	const text = parts
		.filter((part) => part !== link)
		.map((part) => part.text)
		.join('')
		.replace(/\s+/g, ' ')
		.trim();
	return {text, url: link.href, taskId: CLICKUP_TASK_URL.exec(link.href)![2]};
}

/** Task cards look like the ClickUp inbox and Tasks pages: space tile, where it lives, status, due, people. */
const TaskSummary = observer(({draft, options}: {draft: TaskDraft; options: Options | undefined}) => {
	const {t} = useLingui();
	const known = draft.kind === 'task_update' ? KonvoWorkspace.findTask(draft.taskId) : null;
	const listId = draft.kind === 'task' ? draft.listId : (known?.list.id ?? null);
	const updatedId = draft.kind === 'task_update' ? draft.taskId : null;
	useEffect(() => {
		void KonvoWorkspace.loadHierarchy();
		if (updatedId) void KonvoTasks.loadDetail(updatedId);
	}, [updatedId]);
	useEffect(() => {
		if (listId && draft.kind === 'task_update') void KonvoWorkspace.loadListMeta(listId);
	}, [listId, draft.kind]);
	const where = listId ? (KonvoWorkspace.allLists.find((entry) => entry.list.id === listId) ?? null) : null;
	const place = where
		? [where.space.name, where.folder?.name, where.list.name].filter(Boolean).join(' / ')
		: draft.kind === 'task'
			? optionName(options, draft.listId)
			: [known?.space.name, known?.folder.name, known?.list.name].filter(Boolean).join(' / ');
	const statuses = KonvoWorkspace.statusesFor(listId);
	const status =
		draft.kind === 'task_update'
			? (statuses.find((entry) => (entry.status ?? '').toLowerCase() === draft.status.toLowerCase()) ?? {
					status: draft.status,
					color: null,
					type: null,
				})
			: null;
	const people = draft.kind === 'task_update' ? draft.assigneeIds.map((id) => optionName(options, String(id))) : [];
	const estimate = draft.kind === 'task_update' ? draft.timeEstimateMinutes : null;
	// Older update cards carried a missing due date as 1 Jan 1970.
	const due = draft.due && new Date(draft.due).getFullYear() > 2000 ? draft.due : '';
	return (
		<>
			<div className={styles.taskHead}>
				{where ? (
					<SpaceTile name={where.space.name} color={where.space.color} size={30} className={styles.taskTile} />
				) : (
					<span className={clsx(styles.taskTile, styles.taskTileLogo)} aria-hidden>
						<ClickUpLogo size={16} />
					</span>
				)}
				<span className={styles.taskTitles}>
					<span className={styles.title}>{draft.name || t`Untitled task`}</span>
					{place && <span className={styles.taskPlace}>{place}</span>}
				</span>
			</div>
			{(status || due || (estimate ?? 0) > 0 || people.length > 0) && (
				<div className={styles.taskFacts}>
					{status && <StatusPill status={status} statuses={statuses} className={styles.taskStatus} />}
					{due && (
						<span className={styles.taskFact}>
							<CalendarBlankIcon size={14} />
							{formatDue(due)}
						</span>
					)}
					{estimate != null && estimate > 0 && (
						<span className={styles.taskFact}>
							<TimerIcon size={14} />
							{t`${estimate} min`}
						</span>
					)}
					{people.length > 0 && (
						<span className={styles.taskFact} title={people.join(', ')}>
							<span className={styles.taskPeople} aria-hidden>
								{people.slice(0, 3).map((name) => (
									<span
										key={name}
										className={styles.taskAvatar}
										style={{'--avatar-hue': actorHue(name)} as React.CSSProperties}
									>
										{actorInitials(name)}
									</span>
								))}
							</span>
							<span className={styles.taskPeopleNames}>{people.join(', ')}</span>
						</span>
					)}
				</div>
			)}
			<DraftNotes notes={draft.notes} />
			{draft.description.trim() && (
				<span className={styles.preview}>{draft.description.trim().replace(/\n\s*\n+/g, '\n')}</span>
			)}
		</>
	);
});

type MailDraft = Extract<AssistantActionDraft, {kind: 'mail'}>;

/** How a mail goes out when that differs from "now, normal importance": send later, importance, signature. */
function MailCardExtras({draft}: {draft: MailDraft}) {
	const {t, i18n} = useLingui();
	const when = draft.sendAt ? formatScheduledMessage(draft.sendAt, i18n.locale) : null;
	const parts = [
		...(when ? [t`Sends ${when}`] : []),
		...(draft.importance === 'high' ? [t`High importance`] : draft.importance === 'low' ? [t`Low importance`] : []),
		...(draft.signature ? [t`Signature: ${draft.signature}`] : []),
	];
	return parts.length > 0 ? <span className={styles.meta}>{parts.join(' · ')}</span> : null;
}

/** The op card's preview without the title and detail lines the card already shows, and without Markdown bold. */
function opPreviewBody(draft: Extract<AssistantActionDraft, {kind: 'op'}>): string {
	const shown = new Set([draft.title, draft.detail]);
	return (draft.preview ?? '')
		.split('\n')
		.map((line) => line.replace(/\*\*/g, ''))
		.filter((line, index) => !(index < 2 && shown.has(line.trim())))
		.join('\n')
		.trim();
}

/** What the card also changes but cannot edit (tags, a move, an event's place or repeat), as the server wrote it. */
function DraftNotes({notes}: {notes: ReadonlyArray<string> | undefined}) {
	if (!notes?.length) return null;
	return (
		<>
			{notes.map((note) => (
				<span key={note} className={styles.meta}>
					{note}
				</span>
			))}
		</>
	);
}

/** A finished task card: what happened, then Open (in Konvo) and the ClickUp logo button. */
function TaskDone({note}: {note: {text: string; url: string; taskId: string}}) {
	const {t} = useLingui();
	return (
		<div className={styles.taskDone} role="status">
			<CheckCircleIcon size={16} weight="fill" className={styles.taskDoneIcon} />
			<span className={styles.taskDoneText}>{note.text}</span>
			<button
				type="button"
				className={styles.button}
				onClick={() => openKonvoTask(note.taskId)}
				data-flx="konvo-assistant.action-card.open-task"
			>
				<KonvoLogo size={16} />
				{t`Open task`}
			</button>
			<a
				className={styles.clickUpButton}
				href={note.url}
				target="_blank"
				rel="noopener noreferrer"
				title={t`Open in ClickUp`}
				aria-label={t`Open in ClickUp`}
				onClick={(event) => {
					event.preventDefault();
					void openExternalUrl(note.url);
				}}
				data-flx="konvo-assistant.action-card.open-clickup"
			>
				<ClickUpLogo size={18} />
			</a>
		</div>
	);
}

export function AssistantActionCard({cardId}: {cardId: string}) {
	const {t, i18n} = useLingui();
	const [data, setData] = useState<DraftResponse | null>(null);
	const [draft, setDraft] = useState<AssistantActionDraft | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [attempt, setAttempt] = useState(0);
	const [note, setNote] = useState('');
	const [editing, setEditing] = useState(false);
	const [flowFields, setFlowFields] = useState<FlowMailFields | null>(null);
	const endpoint = `/konvo/cards/${encodeURIComponent(cardId)}/draft`;
	useEffect(() => {
		let cancelled = false;
		void http
			.get<DraftResponse>(endpoint, RETRY_RATE_LIMITS)
			.then((response) => {
				if (!cancelled) {
					setData(response.body);
					setDraft(response.body.draft);
					setNote(response.body.note ?? '');
					setError('');
				}
			})
			.catch(() => {
				if (!cancelled) setError(t`Could not load this draft.`);
			});
		return () => {
			cancelled = true;
		};
	}, [endpoint, attempt]);
	// A conversational edit ("make it shorter") updates the same server-side
	// draft. Refresh quiet cards so the revised text appears without replacing
	// the card; never poll over fields the user is actively editing.
	useEffect(() => {
		if (!data || data.flow || data.done || data.expired || editing) return;
		const timer = window.setInterval(() => {
			void http
				.get<DraftResponse>(endpoint)
				.then((response) => {
					if (response.body.revision === data.revision) return;
					setData(response.body);
					setDraft(response.body.draft);
					setNote(response.body.note ?? '');
				})
				.catch(() => undefined);
		}, 4000);
		return () => window.clearInterval(timer);
	}, [data, editing, endpoint]);
	const flowOriginal = data?.flow ? flowMailFields(data.flow) : null;
	useEffect(() => setFlowFields(data?.flow ? flowMailFields(data.flow) : null), [data?.flow]);
	const flowDirty = Boolean(
		flowFields &&
			flowOriginal &&
			(flowFields.text !== flowOriginal.text || flowFields.subject !== flowOriginal.subject),
	);
	const dirty = JSON.stringify(draft) !== JSON.stringify(data?.draft) || flowDirty;
	const locked = busy || Boolean(data?.done) || Boolean(data?.expired);
	const open = data && !data.done && !data.expired;
	const update = (fields: Record<string, unknown>) =>
		setDraft((current) => (current ? ({...current, ...fields} as AssistantActionDraft) : null));
	const save = async () => {
		setBusy(true);
		setError('');
		try {
			if (data?.flow) {
				// A one-time email: only its words change; the server makes a new version to confirm.
				const response = await http.patch<{flow: AssistantFlowCardView; revision: number}>(endpoint, {
					body: {
						revision: data.revision,
						flow_text: flowFields?.text ?? '',
						...(flowFields?.subject != null ? {flow_subject: flowFields.subject} : {}),
					},
				});
				setData((current) => (current ? {...current, ...response.body} : null));
				setNote('');
				setEditing(false);
				return;
			}
			const response = await http.patch<{draft: AssistantActionDraft; revision: number}>(endpoint, {
				body: {draft, revision: data?.revision},
			});
			setData((current) => (current ? {...current, ...response.body} : null));
			setDraft(response.body.draft);
			setNote('');
			setEditing(false);
		} catch {
			setError(t`Could not save. Check the fields, or reload if this draft changed elsewhere.`);
		} finally {
			setBusy(false);
		}
	};
	const act = async (action: string) => {
		setBusy(true);
		setError('');
		try {
			const response = await http.post<{done: boolean; embed?: {description?: string}}>(
				Endpoints.KONVO_CARD_ACTION(cardId, action),
				{body: {revision: data?.revision}},
			);
			setData((current) => (current ? {...current, done: response.body.done} : null));
			setNote(response.body.embed?.description ?? t`Action completed.`);
			setEditing(false);
			// A sent web form comes back with the page the site answered with; a started agent
			// thread with its id, for Open thread.
			if ((draft?.kind === 'web_form' || draft?.kind === 'agent_start') && action === 'confirm')
				setAttempt((value) => value + 1);
		} catch {
			setError(t`Could not confirm this action. Reload to check its latest state before retrying.`);
		} finally {
			setBusy(false);
		}
	};

	const kind = draft?.kind;
	const flow = data?.flow ?? null;
	const agentDraft = isAgentDraft(draft) ? draft : null;
	// A draft without the field keeps every file; removing one lists the rest.
	const keptIds = draft && 'files' in draft ? draft.files : undefined;
	const attached = (data?.files ?? []).filter((file) => !keptIds || keptIds.includes(file.id));
	const editable =
		flowOriginal != null ||
		(draft != null &&
			draft.kind !== 'mail_manage' &&
			draft.kind !== 'op' &&
			!(draft.kind === 'web_form' && !draft.fields.some((field) => field.editable)) &&
			!(draft.kind === 'time' && draft.operation === 'delete') &&
			!(draft.kind === 'calendar' && draft.operation === 'delete') &&
			draft.kind !== 'agent_stop' &&
			draft.kind !== 'agent_approval' &&
			draft.kind !== 'agent_answer' &&
			draft.kind !== 'agent_dismiss' &&
			draft.kind !== 'agent_settle' &&
			draft.kind !== 'agent_rename' &&
			draft.kind !== 'agent_merge');
	const kindLabel = flow
		? flowKindLabel(i18n, flow)
		: agentDraft
			? agentKindLabel(i18n)
			: draft?.kind === 'op'
				? opKindLabel(i18n, draft.area)
				: kind === 'task' || kind === 'task_update'
					? t`Task`
					: kind === 'mail'
						? t`Email`
						: kind === 'mail_manage'
							? t`Email`
							: kind === 'time'
								? t`Time entry`
								: kind === 'calendar'
									? t`Calendar`
									: kind === 'message'
										? t`Message`
										: kind === 'web_form'
											? t`Web form`
											: t`WhatsApp`;
	const KindIcon = flow
		? isFlowMailCard(flow)
			? EnvelopeSimpleIcon
			: IconBolt
		: agentDraft
			? IconTerminal2
			: draft?.kind === 'op'
				? opKindIcon(draft.area)
				: kind === 'task' || kind === 'task_update'
					? ClickUpLogo
					: kind === 'mail' || kind === 'mail_manage'
						? EnvelopeSimpleIcon
						: kind === 'time'
							? TimerIcon
							: kind === 'calendar'
								? CalendarBlankIcon
								: kind === 'web_form'
									? GlobeIcon
									: ChatCircleIcon;
	const confirmLabel = flow
		? flowConfirmLabel(i18n, flow)
		: agentDraft
			? agentConfirmLabel(i18n, agentDraft)
			: kind === 'op'
				? t`Confirm`
				: kind === 'task'
					? t`Create task`
					: kind === 'task_update'
						? t`Update task`
						: kind === 'mail'
							? t`Send email`
							: kind === 'mail_manage'
								? t`Confirm email action`
								: kind === 'time'
									? t`Confirm time`
									: kind === 'calendar'
										? t`Confirm calendar change`
										: kind === 'web_form'
											? t`Send form`
											: t`Send message`;
	const taskNote = (kind === 'task' || kind === 'task_update') && data?.done && note ? taskDoneNote(note) : null;
	const stateLabel = data?.done
		? t`Done`
		: data?.expired
			? t`Expired`
			: dirty
				? t`Unsaved changes`
				: t`Waiting for you`;

	return (
		<article className={styles.card} data-flx="konvo-assistant.action-card">
			<KonvoCardHead
				icon={<KindIcon size={16} />}
				label={draft || flow ? kindLabel : t`Assistant action`}
				end={data ? <span className={clsx(styles.state, data.done && styles.stateDone)}>{stateLabel}</span> : null}
			/>

			{draft && (
				<div className={styles.summary}>
					{(draft.kind === 'task' || draft.kind === 'task_update') && (
						<TaskSummary draft={draft} options={data?.options} />
					)}
					{draft.kind === 'mail' && (
						<>
							<span className={styles.title}>{draft.subject || t`(No subject)`}</span>
							{draft.forward && <span className={styles.meta}>{t`Forwarding: ${draft.forward}`}</span>}
							<span className={styles.meta}>
								{t`To`} {draft.to.join(', ')}
								{draft.cc.length > 0 ? ` · ${t`Cc`} ${draft.cc.join(', ')}` : ''}
								{draft.bcc?.length ? ` · ${t`Bcc`} ${draft.bcc.join(', ')}` : ''}
							</span>
							<MailCardExtras draft={draft} />
							{draft.text && <span className={styles.preview}>{draft.text}</span>}
						</>
					)}
					{draft.kind === 'whatsapp' && (
						<>
							<span className={styles.title}>{optionName(data?.options, draft.jid)}</span>
							{draft.text && <span className={styles.preview}>{draft.text}</span>}
						</>
					)}
					{draft.kind === 'message' && (
						<>
							<span className={styles.title}>{optionName(data?.options, draft.destinationId)}</span>
							{draft.replyTo && <span className={styles.meta}>{t`Replying to a message`}</span>}
							{draft.text && <span className={styles.preview}>{draft.text}</span>}
						</>
					)}
					{draft.kind === 'time' && (
						<>
							<span className={styles.title}>
								{draft.operation === 'delete' ? t`Delete time entry` : t`Time entry`}
							</span>
							<span className={styles.meta}>
								{draft.durationMinutes} min · {formatDue(draft.start)}
							</span>
							{draft.description && <span className={styles.preview}>{draft.description}</span>}
						</>
					)}
					{draft.kind === 'calendar' && (
						<>
							<span className={styles.title}>{draft.subject}</span>
							<span className={styles.meta}>
								{draft.operation === 'delete' ? t`Cancel event` : formatDue(draft.start)}
							</span>
							{draft.attendees.length > 0 && <span className={styles.preview}>{draft.attendees.join(', ')}</span>}
							{draft.operation === 'update' && <DraftNotes notes={draft.notes} />}
						</>
					)}
					{draft.kind === 'mail_manage' && (
						<>
							<span className={styles.title}>{draft.subject}</span>
							<span className={styles.meta}>{draft.operation === 'archive' ? t`Archive email` : t`Mark as read`}</span>
						</>
					)}
					{draft.kind === 'op' && (
						<>
							<span className={styles.title}>{draft.title}</span>
							{draft.detail && <span className={styles.meta}>{draft.detail}</span>}
							{opPreviewBody(draft) && <span className={styles.opPreview}>{opPreviewBody(draft)}</span>}
						</>
					)}
					{draft.kind === 'web_form' && <WebFormSummary draft={draft} edited={(data?.revision ?? 0) > 0} />}
					{agentDraft && <AgentActionSummary draft={agentDraft} />}
				</div>
			)}
			{flow && (
				<div className={styles.summary} data-flx="konvo-assistant.flow-card">
					<FlowSummary view={flow} editing={editing && flowFields != null} />
				</div>
			)}
			{draft && attached.length > 0 && (
				<ul className={styles.files} aria-label={t`Attached files`}>
					{attached.map((file) => {
						const FileKindIcon = file.contentType.startsWith('image/') ? ImageIcon : PaperclipIcon;
						return (
							<li key={file.id} className={styles.file}>
								<FileKindIcon size={14} />
								<span className={styles.fileName}>{file.filename}</span>
								<span className={styles.fileSize}>{formatSize(file.size)}</span>
								{editing && !locked && (
									<button
										type="button"
										className={styles.fileRemove}
										aria-label={t`Remove ${file.filename}`}
										onClick={() =>
											update({files: attached.filter((other) => other.id !== file.id).map((other) => other.id)})
										}
									>
										<XIcon size={12} />
									</button>
								)}
							</li>
						);
					})}
				</ul>
			)}
			{!draft && !flow && !error && <p className={styles.note}>{t`Loading draft…`}</p>}

			{flow && flowFields && editing && (
				<fieldset className={styles.fields} disabled={locked} data-flx="konvo-assistant.flow-card.fields">
					{flowFields.subject != null && (
						<label>
							{t`Subject`}
							<input
								value={flowFields.subject}
								maxLength={255}
								onChange={(e) => setFlowFields({...flowFields, subject: e.target.value})}
							/>
						</label>
					)}
					<label>
						{t`Message`}
						<textarea
							className={styles.letterInput}
							rows={Math.min(16, Math.max(6, flowFields.text.split('\n').length + 1))}
							value={flowFields.text}
							maxLength={20000}
							onChange={(e) => setFlowFields({...flowFields, text: e.target.value})}
							data-flx="konvo-assistant.flow-card.text-input"
						/>
					</label>
				</fieldset>
			)}
			{draft && editing && (
				<fieldset className={styles.fields} disabled={locked}>
					{draft.kind === 'task' && (
						<>
							<label>
								{t`Task name`}
								<input value={draft.name} onChange={(e) => update({name: e.target.value})} />
							</label>
							<label>
								{t`Location`}
								<select value={draft.listId} onChange={(e) => update({listId: e.target.value})}>
									{data?.options.map((option) => (
										<option value={option.id} key={option.id}>
											{option.name}
										</option>
									))}
								</select>
							</label>
							<label>
								{t`Due date (South Africa)`}
								<input type="datetime-local" value={draft.due} onChange={(e) => update({due: e.target.value})} />
							</label>
							<label>
								{t`Description`}
								<textarea rows={5} value={draft.description} onChange={(e) => update({description: e.target.value})} />
							</label>
						</>
					)}
					{draft.kind === 'mail' && (
						<>
							<label>
								{t`To`}
								<input
									value={draft.to.join(', ')}
									onChange={(e) => update({to: e.target.value.split(',').map((s) => s.trim())})}
								/>
							</label>
							<label>
								{t`Cc`}
								<input
									value={draft.cc.join(', ')}
									onChange={(e) => update({cc: e.target.value ? e.target.value.split(',').map((s) => s.trim()) : []})}
								/>
							</label>
							<label>
								{t`Bcc`}
								<input
									value={(draft.bcc ?? []).join(', ')}
									onChange={(e) => update({bcc: e.target.value ? e.target.value.split(',').map((s) => s.trim()) : []})}
								/>
							</label>
							<label>
								{t`Subject`}
								<input value={draft.subject} onChange={(e) => update({subject: e.target.value})} />
							</label>
						</>
					)}
					{draft.kind === 'whatsapp' && (
						<label>
							{t`Recipient`}
							<select value={draft.jid} onChange={(e) => update({jid: e.target.value})}>
								{data?.options.map((option) => (
									<option key={option.id} value={option.id}>
										{option.name}
									</option>
								))}
							</select>
						</label>
					)}
					{draft.kind === 'message' && (
						<label>
							{t`Send with`}
							<select value={draft.destinationId} onChange={(e) => update({destinationId: e.target.value})}>
								{data?.options.map((option) => (
									<option key={option.id} value={option.id}>
										{option.name}
									</option>
								))}
							</select>
						</label>
					)}
					{draft.kind === 'task_update' && (
						<>
							<label>
								{t`Task name`}
								<input value={draft.name} onChange={(e) => update({name: e.target.value})} />
							</label>
							<label>
								{t`Status`}
								<input value={draft.status} onChange={(e) => update({status: e.target.value})} />
							</label>
							<label>
								{t`Due date (South Africa)`}
								<input type="datetime-local" value={draft.due} onChange={(e) => update({due: e.target.value})} />
							</label>
							<label>
								{t`Priority (1–4)`}
								<input
									type="number"
									min={1}
									max={4}
									value={draft.priority ?? ''}
									onChange={(e) => update({priority: e.target.value ? Number(e.target.value) : null})}
								/>
							</label>
							<label>
								{t`Time estimate (minutes)`}
								<input
									type="number"
									min={0}
									value={draft.timeEstimateMinutes ?? ''}
									onChange={(e) => update({timeEstimateMinutes: e.target.value ? Number(e.target.value) : null})}
								/>
							</label>
							<label>
								{t`Assignees`}
								<select
									multiple
									value={draft.assigneeIds.map(String)}
									onChange={(e) =>
										update({assigneeIds: Array.from(e.currentTarget.selectedOptions, (option) => Number(option.value))})
									}
								>
									{data?.options.map((option) => (
										<option key={option.id} value={option.id}>
											{option.name}
										</option>
									))}
								</select>
							</label>
							<label>
								{t`Description`}
								<textarea rows={5} value={draft.description} onChange={(e) => update({description: e.target.value})} />
							</label>
						</>
					)}
					{draft.kind === 'time' && draft.operation !== 'delete' && (
						<>
							<label>
								{t`Start (South Africa)`}
								<input type="datetime-local" value={draft.start} onChange={(e) => update({start: e.target.value})} />
							</label>
							<label>
								{t`Duration (minutes)`}
								<input
									type="number"
									min={1}
									max={1440}
									value={draft.durationMinutes}
									onChange={(e) => update({durationMinutes: Number(e.target.value)})}
								/>
							</label>
							<label>
								{t`Note`}
								<input value={draft.description} onChange={(e) => update({description: e.target.value})} />
							</label>
						</>
					)}
					{draft.kind === 'calendar' && draft.operation === 'update' && (
						<>
							<label>
								{t`Event title`}
								<input value={draft.subject} onChange={(e) => update({subject: e.target.value})} />
							</label>
							<label>
								{t`Start (South Africa)`}
								<input type="datetime-local" value={draft.start} onChange={(e) => update({start: e.target.value})} />
							</label>
							<label>
								{t`End (South Africa)`}
								<input type="datetime-local" value={draft.end} onChange={(e) => update({end: e.target.value})} />
							</label>
							<label>
								{t`Attendees`}
								<input
									value={draft.attendees.join(', ')}
									onChange={(e) =>
										update({attendees: e.target.value ? e.target.value.split(',').map((s) => s.trim()) : []})
									}
								/>
							</label>
							<label>
								{t`Meeting type`}
								<select value={draft.meetingType} onChange={(e) => update({meetingType: e.target.value})}>
									<option value="none">{t`No meeting link`}</option>
									<option value="teams">{t`Teams`}</option>
									<option value="konvo">{t`Konvo`}</option>
								</select>
							</label>
							{draft.meetingType === 'konvo' && (
								<label>
									{t`Konvo channel id`}
									<input value={draft.channelId} onChange={(e) => update({channelId: e.target.value})} />
								</label>
							)}
						</>
					)}
					{draft.kind === 'web_form' && <WebFormFields draft={draft} onChange={(fields) => update({fields})} />}
					{agentDraft && <AgentActionFields draft={agentDraft} onChange={update} />}
					{(draft.kind === 'mail' || draft.kind === 'whatsapp' || draft.kind === 'message') && (
						<label>
							{t`Message`}
							<textarea rows={6} value={draft.text} onChange={(e) => update({text: e.target.value})} />
						</label>
					)}
				</fieldset>
			)}

			{error && (
				<p className={styles.error} role="alert">
					{error}
					<button type="button" className={clsx(styles.button, styles.quiet)} onClick={() => setAttempt((v) => v + 1)}>
						{t`Reload draft`}
					</button>
				</p>
			)}
			{note && taskNote && <TaskDone note={taskNote} />}
			{note && !taskNote && (
				<p className={styles.note} role="status">
					<NoteText value={note} />
				</p>
			)}
			{agentDraft && data?.done && note && (
				<div className={styles.agentDone}>
					<AgentActionOpen draft={agentDraft} />
				</div>
			)}
			{data?.expired && <p className={styles.note}>{t`This draft expired. Ask the assistant for a new one.`}</p>}

			{open && (
				<footer className={styles.footer}>
					{editable && (
						<button
							type="button"
							className={clsx(styles.button, styles.quiet)}
							aria-expanded={editing}
							disabled={busy}
							onClick={() => setEditing((v) => !v)}
							data-flx="konvo-assistant.action-card.edit"
						>
							<PencilSimpleIcon size={14} />
							{editing ? t`Hide fields` : t`Edit`}
						</button>
					)}
					<span className={styles.spacer} />
					<button type="button" className={styles.button} disabled={busy} onClick={() => void act('cancel')}>
						{t`Cancel`}
					</button>
					{dirty && (
						<button type="button" className={styles.button} disabled={busy} onClick={() => void save()}>
							{t`Save changes`}
						</button>
					)}
					<button
						type="button"
						className={clsx(styles.button, styles.primary)}
						disabled={busy || dirty}
						title={dirty ? t`Save your changes first` : undefined}
						onClick={() => void act('confirm')}
					>
						{confirmLabel}
					</button>
				</footer>
			)}
		</article>
	);
}
